/**
 * BULWARK — the power grappler. Move table.
 *
 * Discipline: *Hold Iron*, an invented welding of pit wrestling to industrial-yard brawling.
 * 1.98 m, 128 kg. See src/combat/roster.js for stats and ARCHITECTURE.md §16 for the contract.
 *
 * DESIGN INTENT
 * -------------
 * Bulwark wins by removing the opponent's options one wall at a time. The frame data is built to
 * make that a fair trade rather than a free one:
 *
 *  - Fastest move is i12 where Kestrel has i10. Bulwark can never win the jab war and is not
 *    meant to. Every exchange Bulwark takes at close range is a guess they are paid for.
 *  - Longest reach and highest damage in the game, at the cost of recovery. A whiffed command
 *    throw is -30 and a whiffed `bw-tectonic` is a free round. Nothing here is cheap.
 *  - Wall pressure is the win condition: five moves carry to the wall or splat outright.
 *  - Grit builds from damage TAKEN (0.16 per point), so a losing Bulwark unlocks `bw-gritslam`
 *    and the finisher. The comeback is real but capped — Grit buys one big read, not a reset.
 *  - Anchor stance grants one armor hit on designated moves at 45 % damage, and slows movement
 *    by 18 %. Armor is a commitment: every armored move here is -14 or worse on block.
 *
 * Deliberate holes: no fast panic button (fastest reversal is i16 with no invulnerability), poor
 * whiff punishment (nothing under i14 reaches past 1.0 m), and slow lows (fastest low is i14 and
 * -12 on block). Kestrel's answer to Bulwark is to never be at the wall and never be predictable.
 *
 * THROW CHAINS
 * ------------
 * `bw-holdiron` → `bw-hold-lift` → `bw-hold-slam`. The combat system reads a throw's `cancels`
 * during THROW_EXECUTE and requires a timed HP/HK press to continue, with each stage harder to
 * break (14 → 5 frames) and scaling down in damage. Missing the timing simply ends the chain.
 *
 * Frame data is in 60 Hz frames. `hitAdvantage`/`blockAdvantage` are DERIVED by the schema as
 * `stun - (active + recovery - 1)`; intended values are in comments.
 */

/** Compact hitbox constructor. Fighter local space, metres: +X forward, +Y up from the ground. */
const hb = (from, to, joint, ox, oy, ex, ey, r, extra = {}) => ({
  from, to, joint, ox, oy, oz: 0, ex, ey, ez: 0, r, ...extra,
});

export const BULWARK_MOVES = [
  // ===========================================================================
  // BASIC STRIKES — slower than Kestrel's, but each one moves the opponent.
  // ===========================================================================
  {
    id: 'bw-jab', name: 'Short Iron', input: '1', category: 'strike',
    startup: 12, active: 2, recovery: 12,           // blk -3, hit +4
    level: 'mid', damage: 13, hitstun: 17, blockstun: 10,
    pushbackHit: 0.16, pushbackBlock: 0.22, selfPushbackBlock: 0.05,
    cancelWindow: [13, 26], cancels: ['bw-cross', 'bw-lowscrape'], delayableUntil: 6,
    hitboxes: [hb(13, 14, 'handL', 0.32, 1.56, 0.74, 1.54, 0.12, { tracking: 0.6, priority: 2 })],
    counterHit: { damageMul: 1.25, hitstunBonus: 7, forcesState: 'hitstun-stand' },
    scaling: { starterValue: 1.0, cost: 0.09, minScale: 0.22 },
    tracking: 0.6, anim: 'bw.jab',
    sfx: { swing: 'swing-light', hit: 'impact-medium', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: 'arc-thin' },
    tags: ['jab', 'string-starter'],
    aiHints: { range: 0.86, risk: 0.14, use: ['pressure', 'poke'] },
  },
  {
    id: 'bw-cross', name: 'Short Iron, Drive', input: '2', category: 'strike',
    followUpOf: 'bw-jab',
    startup: 14, active: 3, recovery: 20,           // blk -12, hit +2
    level: 'mid', damage: 20, hitstun: 24, blockstun: 10,
    pushbackHit: 0.30, pushbackBlock: 0.36, selfPushbackBlock: 0.12,
    hitboxes: [hb(15, 17, 'handR', 0.30, 1.52, 0.94, 1.50, 0.14, { tracking: 0.5, priority: 3 })],
    counterHit: { damageMul: 1.35, hitstunBonus: 11, forcesState: 'hitstun-stand' },
    onHit: { wallSplat: true },
    scaling: { starterValue: 0.88, cost: 0.12, minScale: 0.22 },
    anim: 'bw.cross',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-medium', trail: 'arc-wide' },
    tags: ['string-ender', 'wall', 'delayable'],
    aiHints: { range: 1.08, risk: 0.55, use: ['pressure', 'wall'] },
  },
  {
    id: 'bw-straight', name: 'Long Iron', input: '2', category: 'strike',
    startup: 16, active: 3, recovery: 19,           // blk -12, hit +1
    level: 'mid', damage: 22, hitstun: 23, blockstun: 10,
    pushbackHit: 0.30, pushbackBlock: 0.38,
    hitboxes: [hb(17, 19, 'handR', 0.34, 1.54, 1.14, 1.50, 0.14, { tracking: 0.55, priority: 3 })],
    counterHit: { damageMul: 1.4, hitstunBonus: 12, forcesState: 'hitstun-stand' },
    onHit: { wallSplat: true },
    scaling: { starterValue: 0.88, cost: 0.12, minScale: 0.22 },
    anim: 'bw.straight',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-medium', trail: 'arc-wide' },
    tags: ['poke', 'range', 'wall'],
    aiHints: { range: 1.28, risk: 0.5, use: ['poke', 'range', 'wall'] },
  },
  {
    id: 'bw-hammer', name: 'Hammer Fall', input: 'f+2', category: 'strike',
    startup: 22, active: 4, recovery: 26,           // blk -18, hit +7
    level: 'mid', damage: 34, hitstun: 36, blockstun: 11,
    pushbackHit: 0.36, pushbackBlock: 0.44, selfPushbackBlock: 0.16,
    armor: { from: 8, to: 21, hits: 1, damageMul: 0.45 },
    hitboxes: [hb(23, 26, 'handR', 0.26, 1.80, 0.86, 0.96, 0.17, { tracking: 0.6, priority: 4 })],
    counterHit: { damageMul: 1.4, hitstunBonus: 14, forcesState: 'hitstun-stand' },
    onHit: { knockdown: 'hard', wallSplat: true },
    scaling: { starterValue: 0.78, cost: 0.15, minScale: 0.22 },
    anim: 'bw.hammer',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['armor', 'wall', 'knockdown', 'commitment'],
    aiHints: { range: 1.03, risk: 0.82, use: ['pressure', 'wall', 'punish'] },
  },
  {
    id: 'bw-backhand', name: 'Turning Iron', input: 'b+2', category: 'strike',
    startup: 20, active: 3, recovery: 22,           // blk -14, hit +4
    level: 'high', damage: 28, hitstun: 28, blockstun: 10,
    pushbackHit: 0.34, pushbackBlock: 0.40,
    hitboxes: [hb(21, 23, 'handR', 0.20, 1.70, 1.02, 1.66, 0.16, { tracking: 0.95, priority: 4 })],
    counterHit: { damageMul: 1.45, hitstunBonus: 16, forcesState: 'hitstun-stand' },
    onHit: { knockdown: 'soft', wallSplat: true },
    scaling: { starterValue: 0.82, cost: 0.13, minScale: 0.22 },
    tracking: 0.95, anim: 'bw.backhand',
    sfx: { swing: 'swing-whoosh', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['anti-sidestep', 'wall', 'duckable', 'knockdown'],
    aiHints: { range: 1.18, risk: 0.68, use: ['anti-sidestep', 'wall'] },
  },
  {
    id: 'bw-bootkick', name: 'Boot', input: '4', category: 'strike',
    startup: 17, active: 3, recovery: 20,           // blk -13, hit +1
    level: 'mid', damage: 24, hitstun: 24, blockstun: 10,
    pushbackHit: 0.34, pushbackBlock: 0.42,
    hitboxes: [hb(18, 20, 'footR', 0.36, 1.24, 1.22, 1.30, 0.15, { tracking: 0.5, priority: 3 })],
    counterHit: { damageMul: 1.35, hitstunBonus: 12, forcesState: 'hitstun-stand' },
    scaling: { starterValue: 0.86, cost: 0.12, minScale: 0.22 },
    anim: 'bw.bootkick',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-medium', trail: 'arc-wide' },
    tags: ['poke', 'range'],
    aiHints: { range: 1.37, risk: 0.5, use: ['poke', 'range'] },
  },
  {
    id: 'bw-kneecrush', name: 'Knee Crush', input: 'f+4', category: 'strike',
    startup: 18, active: 3, recovery: 18,           // blk -11, hit +6
    level: 'mid', damage: 26, hitstun: 26, blockstun: 9,
    pushbackHit: 0.14, pushbackBlock: 0.26, selfPushbackBlock: 0.04,
    moveForward: 0.42,
    armor: { from: 6, to: 17, hits: 1, damageMul: 0.5 },
    hitboxes: [hb(19, 21, 'kneeR', 0.22, 1.24, 0.62, 1.34, 0.16, { tracking: 0.85, priority: 4 })],
    counterHit: { damageMul: 1.5, hitstunBonus: 16, forcesState: 'hitstun-stand' },
    onHit: { wallSplat: true },
    scaling: { starterValue: 0.84, cost: 0.12, minScale: 0.22 },
    tracking: 0.85, anim: 'bw.kneecrush',
    sfx: { swing: 'swing-heavy', hit: 'impact-body', block: 'guard-heavy' },
    fx: { hit: 'spark-medium', trail: '' },
    tags: ['armor', 'approach', 'wall', 'counter-starter'],
    aiHints: { range: 0.85, risk: 0.6, use: ['pressure', 'wall'] },
  },
  {
    id: 'bw-heelturn', name: 'Heel Turn', input: 'b+4', category: 'strike',
    startup: 24, active: 4, recovery: 26,           // blk -19, hit +5
    level: 'mid', damage: 36, hitstun: 34, blockstun: 10,
    pushbackHit: 0.40, pushbackBlock: 0.46,
    hitboxes: [hb(25, 28, 'footL', 0.18, 1.10, 1.24, 1.56, 0.17, { tracking: 1.0, priority: 5 })],
    counterHit: { damageMul: 1.45, hitstunBonus: 18, forcesState: 'hitstun-stand' },
    onHit: { knockdown: 'hard', wallSplat: true },
    scaling: { starterValue: 0.76, cost: 0.16, minScale: 0.22 },
    tracking: 1.0, anim: 'bw.heelturn',
    sfx: { swing: 'swing-whoosh', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['anti-sidestep', 'wall', 'knockdown', 'commitment'],
    aiHints: { range: 1.41, risk: 0.85, use: ['anti-sidestep', 'wall', 'anti-air'] },
  },

  // ===========================================================================
  // LOWS — slow and punishable, but they carry weight.
  // ===========================================================================
  {
    id: 'bw-lowkick', name: 'Boot Scrape', input: '3', category: 'strike',
    startup: 14, active: 2, recovery: 18,           // blk -12, hit -2
    level: 'low', damage: 15, hitstun: 17, blockstun: 7,
    pushbackHit: 0.16, pushbackBlock: 0.26,
    hitsAir: false,
    hitboxes: [hb(15, 16, 'footR', 0.28, 0.22, 0.82, 0.18, 0.13, { tracking: 0.55, priority: 2 })],
    counterHit: { damageMul: 1.3, hitstunBonus: 9, forcesState: 'hitstun-crouch' },
    scaling: { starterValue: 0.95, cost: 0.10, minScale: 0.22 },
    anim: 'bw.lowkick',
    sfx: { swing: 'swing-light', hit: 'impact-body', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: '' },
    tags: ['low', 'poke'],
    aiHints: { range: 0.95, risk: 0.4, use: ['poke', 'mixup'] },
  },
  {
    id: 'bw-lowscrape', name: 'Short Iron, Scrape', input: '3', category: 'strike',
    followUpOf: 'bw-jab',
    startup: 16, active: 2, recovery: 22,           // blk -16, hit -6
    level: 'low', damage: 17, hitstun: 17, blockstun: 7,
    pushbackHit: 0.18, pushbackBlock: 0.28,
    hitsAir: false,
    hitboxes: [hb(17, 18, 'footL', 0.26, 0.20, 0.84, 0.16, 0.13, { tracking: 0.5, priority: 2 })],
    counterHit: { damageMul: 1.3, hitstunBonus: 10, forcesState: 'hitstun-crouch' },
    scaling: { starterValue: 0.9, cost: 0.11, minScale: 0.22 },
    anim: 'bw.lowscrape',
    sfx: { swing: 'swing-light', hit: 'impact-body', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: '' },
    tags: ['low', 'string-ender', 'mixup'],
    aiHints: { range: 0.97, risk: 0.62, use: ['mixup', 'pressure'] },
  },
  {
    id: 'bw-lowsweep', name: 'Yard Sweep', input: 'd+3', category: 'strike',
    startup: 20, active: 3, recovery: 27,           // blk -21, hit -6
    level: 'low', damage: 24, hitstun: 23, blockstun: 8,
    pushbackHit: 0.26, pushbackBlock: 0.34,
    hitsAir: false,
    hitboxes: [hb(21, 23, 'footR', 0.18, 0.16, 1.06, 0.14, 0.15, { tracking: 0.8, priority: 3 })],
    counterHit: { damageMul: 1.35, hitstunBonus: 12, forcesState: '' },
    onHit: { knockdown: 'hard' },
    scaling: { starterValue: 0.85, cost: 0.13, minScale: 0.22 },
    tracking: 0.8, anim: 'bw.lowsweep',
    sfx: { swing: 'swing-whoosh', hit: 'impact-body', block: 'guard-light' },
    fx: { hit: 'spark-medium', trail: 'arc-wide' },
    tags: ['low', 'knockdown', 'oki'],
    aiHints: { range: 1.21, risk: 0.8, use: ['mixup', 'oki'] },
  },
  {
    id: 'bw-lowscrape2', name: 'Ground Rake', input: 'db+3', category: 'strike',
    startup: 15, active: 2, recovery: 19,           // blk -13, hit -3
    level: 'low', damage: 16, hitstun: 17, blockstun: 7,
    pushbackHit: 0.14, pushbackBlock: 0.24,
    hitsAir: false, hitsGrounded: true,
    hitboxes: [hb(16, 17, 'footL', 0.24, 0.14, 0.80, 0.12, 0.13, { tracking: 0.55, priority: 2 })],
    counterHit: { damageMul: 1.3, hitstunBonus: 9, forcesState: 'hitstun-crouch' },
    scaling: { starterValue: 0.92, cost: 0.10, minScale: 0.22 },
    anim: 'bw.groundrake',
    sfx: { swing: 'swing-light', hit: 'impact-body', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: '' },
    tags: ['low', 'oki', 'hits-grounded'],
    aiHints: { range: 0.93, risk: 0.45, use: ['oki', 'mixup'] },
  },
  {
    id: 'bw-crouchjab', name: 'Low Iron', input: 'd+1', category: 'strike',
    startup: 13, active: 2, recovery: 13,           // blk -5, hit +4
    level: 'special-mid', damage: 13, hitstun: 18, blockstun: 9,
    pushbackHit: 0.14, pushbackBlock: 0.22,
    hitboxes: [hb(14, 15, 'handR', 0.30, 1.10, 0.76, 1.12, 0.12, { tracking: 0.6, priority: 2 })],
    counterHit: { damageMul: 1.3, hitstunBonus: 8, forcesState: 'hitstun-stand' },
    scaling: { starterValue: 1.0, cost: 0.09, minScale: 0.22 },
    anim: 'bw.crouchjab',
    sfx: { swing: 'swing-light', hit: 'impact-medium', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: '' },
    tags: ['crouch', 'special-mid', 'anti-crouch'],
    aiHints: { range: 0.88, risk: 0.2, use: ['pressure', 'anti-crouch'] },
  },
  {
    id: 'bw-crouchsmash', name: 'Pit Smash', input: 'd+2', category: 'strike',
    startup: 19, active: 3, recovery: 25,           // blk -19, hit +4
    level: 'special-mid', damage: 28, hitstun: 31, blockstun: 8,
    pushbackHit: 0.20, pushbackBlock: 0.36,
    armor: { from: 7, to: 18, hits: 1, damageMul: 0.45 },
    hitboxes: [hb(20, 22, 'handR', 0.28, 1.02, 0.72, 1.66, 0.16, { tracking: 0.6, priority: 4 })],
    counterHit: { damageMul: 1.4, hitstunBonus: 14, forcesState: '' },
    onHit: { launch: 7.4, juggleGravityMul: 1.02 },
    scaling: { starterValue: 0.76, cost: 0.13, minScale: 0.22 },
    anim: 'bw.crouchsmash',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-wide' },
    tags: ['launcher', 'armor', 'crouch', 'special-mid', 'anti-crouch'],
    aiHints: { range: 0.92, risk: 0.8, use: ['launch', 'punish', 'anti-crouch'] },
  },
  {
    id: 'bw-stomp', name: 'Yard Stomp', input: 'd+4', category: 'strike',
    startup: 21, active: 3, recovery: 22,           // blk -16, hit +3
    level: 'mid', damage: 30, hitstun: 27, blockstun: 9,
    pushbackHit: 0.18, pushbackBlock: 0.32,
    hitsGrounded: true,
    hitboxes: [hb(22, 24, 'footR', 0.46, 1.10, 0.56, 0.10, 0.16, { tracking: 0.6, priority: 4 })],
    counterHit: { damageMul: 1.3, hitstunBonus: 10, forcesState: '' },
    onHit: { knockdown: 'hard' },
    scaling: { starterValue: 0.8, cost: 0.14, minScale: 0.22 },
    anim: 'bw.stomp',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'ground-crack', trail: 'arc-wide' },
    tags: ['oki', 'hits-grounded', 'knockdown', 'meaty'],
    aiHints: { range: 0.82, risk: 0.65, use: ['oki', 'meaty'] },
  },
  {
    id: 'bw-shinbreak', name: 'Shin Break', input: 'df+3', category: 'strike',
    startup: 16, active: 2, recovery: 17,           // blk -11, hit +5
    level: 'mid', damage: 21, hitstun: 23, blockstun: 8,
    pushbackHit: 0.12, pushbackBlock: 0.24, selfPushbackBlock: 0.06,
    moveForward: 0.28,
    hitboxes: [hb(17, 18, 'kneeL', 0.24, 1.18, 0.66, 1.24, 0.15, { tracking: 0.9, priority: 3 })],
    counterHit: { damageMul: 1.45, hitstunBonus: 14, forcesState: 'hitstun-stand' },
    scaling: { starterValue: 0.9, cost: 0.11, minScale: 0.22 },
    tracking: 0.9, anim: 'bw.shinbreak',
    sfx: { swing: 'swing-light', hit: 'impact-body', block: 'guard-light' },
    fx: { hit: 'spark-medium', trail: '' },
    tags: ['anti-sidestep', 'counter-starter'],
    aiHints: { range: 0.89, risk: 0.42, use: ['pressure', 'anti-sidestep'] },
  },

  // ===========================================================================
  // LAUNCHERS AND JUGGLE EXTENDERS
  // ===========================================================================
  {
    id: 'bw-uppercut', name: 'Rising Iron', input: 'df+2', category: 'strike',
    startup: 18, active: 3, recovery: 26,           // blk -20, hit +2
    level: 'mid', damage: 26, hitstun: 30, blockstun: 8,
    pushbackHit: 0.12, pushbackBlock: 0.40,
    hitboxes: [hb(19, 21, 'handR', 0.24, 1.28, 0.68, 1.92, 0.16, { tracking: 0.6, priority: 4 })],
    counterHit: { damageMul: 1.4, hitstunBonus: 14, forcesState: '' },
    onHit: { launch: 9.2, juggleGravityMul: 0.96 },
    scaling: { starterValue: 0.72, cost: 0.13, minScale: 0.22 },
    anim: 'bw.uppercut',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['launcher', 'combo-starter'],
    aiHints: { range: 0.94, risk: 0.85, use: ['launch', 'punish'] },
  },
  {
    id: 'bw-riseknee', name: 'Girder Knee', input: 'df+4', category: 'strike',
    startup: 20, active: 3, recovery: 28,           // blk -22, hit +1
    level: 'mid', damage: 28, hitstun: 31, blockstun: 9,
    pushbackHit: 0.10, pushbackBlock: 0.42,
    armor: { from: 8, to: 19, hits: 1, damageMul: 0.45 },
    hitboxes: [hb(21, 23, 'kneeR', 0.22, 1.20, 0.70, 1.82, 0.16, { tracking: 0.7, priority: 4 })],
    counterHit: { damageMul: 1.4, hitstunBonus: 16, forcesState: '' },
    onHit: { launch: 8.6, juggleGravityMul: 0.94 },
    scaling: { starterValue: 0.72, cost: 0.13, minScale: 0.22 },
    tracking: 0.7, anim: 'bw.riseknee',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['launcher', 'armor', 'commitment'],
    aiHints: { range: 0.92, risk: 0.9, use: ['launch', 'punish'] },
  },
  {
    id: 'bw-juggle-boot', name: 'Girder Boot', input: 'u+4', category: 'strike',
    startup: 14, active: 3, recovery: 22,           // blk -16, hit -3
    level: 'mid', damage: 18, hitstun: 21, blockstun: 8,
    pushbackHit: 0.10, pushbackBlock: 0.26,
    juggleOnly: true,
    hitboxes: [hb(15, 17, 'footR', 0.30, 1.50, 1.06, 1.84, 0.16, { tracking: 0.45, priority: 3 })],
    onHit: { juggleGravityMul: 0.9 },
    scaling: { starterValue: 0.7, cost: 0.11, minScale: 0.22 },
    anim: 'bw.juggleboot',
    sfx: { swing: 'swing-heavy', hit: 'impact-medium', block: 'guard-light' },
    fx: { hit: 'spark-medium', trail: 'arc-wide' },
    tags: ['juggle', 'extender'],
    aiHints: { range: 1.22, risk: 0.55, use: ['launch'] },
  },
  {
    id: 'bw-juggle-hammer', name: 'Overhead Iron', input: 'u+2', category: 'strike',
    startup: 18, active: 3, recovery: 26,           // blk -20, hit -6
    level: 'mid', damage: 30, hitstun: 22, blockstun: 8,
    pushbackHit: 0.34, pushbackBlock: 0.34,
    hitsGrounded: true,
    hitboxes: [hb(19, 21, 'handR', 0.30, 1.90, 0.62, 0.46, 0.17, { tracking: 0.5, priority: 4 })],
    onHit: { knockdown: 'hard', wallSplat: true, juggleGravityMul: 1.8 },
    counterHit: { damageMul: 1.25, hitstunBonus: 8, forcesState: '' },
    scaling: { starterValue: 0.7, cost: 0.16, minScale: 0.22 },
    anim: 'bw.jugglehammer',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['juggle', 'ender', 'wall', 'knockdown'],
    aiHints: { range: 0.96, risk: 0.85, use: ['launch', 'oki'] },
  },

  // ===========================================================================
  // APPROACH AND SPECIALS
  // ===========================================================================
  {
    id: 'bw-shoulder', name: 'Closing Shoulder', input: 'ff+2', category: 'strike',
    startup: 19, active: 3, recovery: 21,           // blk -15, hit +6
    level: 'mid', damage: 30, hitstun: 29, blockstun: 9,
    pushbackHit: 0.24, pushbackBlock: 0.36,
    moveForward: 0.85,
    armor: { from: 5, to: 18, hits: 1, damageMul: 0.4 },
    hitboxes: [hb(20, 22, 'torso', 0.24, 1.40, 0.78, 1.44, 0.20, { tracking: 0.75, priority: 4 })],
    counterHit: { damageMul: 1.45, hitstunBonus: 16, forcesState: 'hitstun-stand' },
    onHit: { wallSplat: true, knockdown: 'none' },
    scaling: { starterValue: 0.82, cost: 0.13, minScale: 0.22 },
    tracking: 0.75, anim: 'bw.shoulder',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-wide' },
    tags: ['armor', 'approach', 'wall', 'commitment'],
    aiHints: { range: 1.55, risk: 0.7, use: ['pressure', 'wall'] },
  },
  {
    id: 'bw-runknee', name: 'Running Girder', input: 'ff+4', category: 'strike',
    startup: 21, active: 3, recovery: 24,           // blk -18, hit +4
    level: 'mid', damage: 32, hitstun: 30, blockstun: 9,
    pushbackHit: 0.30, pushbackBlock: 0.40,
    moveForward: 1.10,
    hitboxes: [hb(22, 24, 'kneeR', 0.26, 1.30, 0.84, 1.38, 0.18, { tracking: 0.6, priority: 4 })],
    counterHit: { damageMul: 1.4, hitstunBonus: 14, forcesState: 'hitstun-stand' },
    onHit: { knockdown: 'hard', wallSplat: true },
    scaling: { starterValue: 0.8, cost: 0.14, minScale: 0.22 },
    anim: 'bw.runknee',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['approach', 'wall', 'knockdown', 'commitment'],
    aiHints: { range: 1.72, risk: 0.8, use: ['pressure', 'wall', 'range'] },
  },
  {
    id: 'bw-ironpalm', name: 'Iron Palm', input: 'qcf+2', category: 'special',
    startup: 20, active: 3, recovery: 30,           // blk -24, hit +6
    level: 'mid', damage: 38, hitstun: 38, blockstun: 8,
    pushbackHit: 0.40, pushbackBlock: 0.48,
    moveForward: 0.45,
    armor: { from: 6, to: 19, hits: 1, damageMul: 0.4 },
    hitboxes: [hb(21, 23, 'handL', 0.26, 1.48, 1.00, 1.46, 0.17, { tracking: 0.8, priority: 5 })],
    counterHit: { damageMul: 1.5, hitstunBonus: 18, forcesState: 'hitstun-stand' },
    onHit: { wallSplat: true, knockdown: 'none' },
    scaling: { starterValue: 0.76, cost: 0.14, minScale: 0.22 },
    meterGain: { hit: 8, block: 3, whiff: 2 },
    tracking: 0.8, anim: 'bw.ironpalm',
    sfx: { swing: 'swing-whoosh', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['special', 'armor', 'wall', 'commitment'],
    aiHints: { range: 1.26, risk: 0.88, use: ['punish', 'wall'] },
  },
  {
    id: 'bw-gritslam', name: 'Grit Slam', input: 'qcb+2', category: 'special',
    startup: 16, active: 3, recovery: 30,           // blk -26, hit +2
    level: 'mid', damage: 44, hitstun: 34, blockstun: 6,
    pushbackHit: 0.30, pushbackBlock: 0.50,
    moveForward: 0.60, meterCost: 55,
    armor: { from: 3, to: 15, hits: 2, damageMul: 0.35 },
    hitboxes: [hb(17, 19, 'handR', 0.22, 1.44, 0.98, 1.50, 0.19, { tracking: 0.9, priority: 6 })],
    counterHit: { damageMul: 1.4, hitstunBonus: 16, forcesState: 'hitstun-stand' },
    onHit: { launch: 8.0, wallSplat: true, juggleGravityMul: 1.0 },
    scaling: { starterValue: 0.68, cost: 0.15, minScale: 0.22 },
    meterGain: { hit: 0, block: 0, whiff: 0 },
    tracking: 0.9, anim: 'bw.gritslam',
    sfx: { swing: 'swing-whoosh', hit: 'impact-counter', block: 'guard-heavy' },
    fx: { hit: 'spark-counter', trail: 'arc-heavy' },
    tags: ['special', 'meter', 'armor', 'launcher', 'comeback'],
    aiHints: { range: 1.24, risk: 0.92, use: ['punish', 'launch', 'panic'] },
  },
  {
    id: 'bw-anchor-charge', name: 'Set Anchor', input: 'qcf+4', category: 'strike',
    startup: 26, active: 4, recovery: 24,           // blk -15, hit +11
    level: 'special-mid', damage: 34, hitstun: 38, blockstun: 12,
    pushbackHit: 0.20, pushbackBlock: 0.30, selfPushbackBlock: 0.08,
    moveForward: 0.50,
    armor: { from: 4, to: 25, hits: 2, damageMul: 0.4 },
    hitboxes: [hb(27, 30, 'torso', 0.22, 1.20, 0.82, 1.52, 0.20, { tracking: 0.85, priority: 5 })],
    counterHit: { damageMul: 1.5, hitstunBonus: 20, forcesState: 'hitstun-stand' },
    onHit: { wallSplat: true },
    scaling: { starterValue: 0.78, cost: 0.15, minScale: 0.22 },
    tracking: 0.85, anim: 'bw.anchorcharge',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['armor', 'special-mid', 'wall', 'commitment', 'anti-crouch'],
    aiHints: { range: 1.02, risk: 0.86, use: ['pressure', 'wall'] },
  },

  // ===========================================================================
  // ANCHOR STANCE AND STANCE-ONLY MOVES
  // ===========================================================================
  {
    id: 'bw-anchor', name: 'Anchor', input: '5', category: 'stance',
    startup: 14, active: 1, recovery: 6,
    level: 'mid', damage: 0, hitstun: 0, blockstun: 0,
    stanceEnter: 'anchor',
    hitboxes: [],
    scaling: { starterValue: 1.0, cost: 0, minScale: 0.22 },
    meterGain: { hit: 0, block: 0, whiff: 0 },
    anim: 'bw.anchor',
    sfx: { swing: 'cloth', hit: 'impact-light', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: '' },
    tags: ['stance'],
    aiHints: { range: 0, risk: 0.45, use: ['pressure'] },
  },
  {
    id: 'bw-anchor-cross', name: 'Anchored Drive', input: '2', category: 'strike',
    stanceRequired: 'anchor',
    startup: 15, active: 3, recovery: 20,           // blk -14, hit +6
    level: 'mid', damage: 28, hitstun: 28, blockstun: 8,
    pushbackHit: 0.30, pushbackBlock: 0.40,
    armor: { from: 4, to: 14, hits: 1, damageMul: 0.45 },
    hitboxes: [hb(16, 18, 'handR', 0.30, 1.52, 1.06, 1.48, 0.15, { tracking: 0.6, priority: 4 })],
    counterHit: { damageMul: 1.45, hitstunBonus: 16, forcesState: 'hitstun-stand' },
    onHit: { wallSplat: true },
    scaling: { starterValue: 0.84, cost: 0.12, minScale: 0.22 },
    anim: 'bw.anchorcross',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-wide' },
    tags: ['anchor', 'armor', 'wall'],
    aiHints: { range: 1.21, risk: 0.62, use: ['pressure', 'wall'] },
  },
  {
    id: 'bw-anchor-hammer', name: 'Anchored Fall', input: 'f+2', category: 'strike',
    stanceRequired: 'anchor',
    startup: 20, active: 4, recovery: 26,           // blk -19, hit +9
    level: 'mid', damage: 40, hitstun: 38, blockstun: 10,
    pushbackHit: 0.38, pushbackBlock: 0.46,
    armor: { from: 4, to: 19, hits: 2, damageMul: 0.4 },
    hitboxes: [hb(21, 24, 'handR', 0.26, 1.84, 0.90, 0.92, 0.18, { tracking: 0.65, priority: 5 })],
    counterHit: { damageMul: 1.45, hitstunBonus: 18, forcesState: 'hitstun-stand' },
    onHit: { knockdown: 'hard', wallSplat: true },
    scaling: { starterValue: 0.74, cost: 0.16, minScale: 0.22 },
    anim: 'bw.anchorhammer',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['anchor', 'armor', 'wall', 'knockdown', 'commitment'],
    aiHints: { range: 1.08, risk: 0.84, use: ['wall', 'punish'] },
  },
  {
    id: 'bw-anchor-riser', name: 'Anchored Rise', input: 'df+2', category: 'strike',
    stanceRequired: 'anchor',
    startup: 16, active: 3, recovery: 26,           // blk -20, hit +4
    level: 'mid', damage: 28, hitstun: 32, blockstun: 8,
    pushbackHit: 0.12, pushbackBlock: 0.40,
    armor: { from: 3, to: 15, hits: 1, damageMul: 0.45 },
    hitboxes: [hb(17, 19, 'handL', 0.24, 1.26, 0.70, 1.94, 0.16, { tracking: 0.65, priority: 5 })],
    counterHit: { damageMul: 1.4, hitstunBonus: 14, forcesState: '' },
    onHit: { launch: 9.4, juggleGravityMul: 0.94 },
    scaling: { starterValue: 0.7, cost: 0.13, minScale: 0.22 },
    anim: 'bw.anchorriser',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['anchor', 'armor', 'launcher'],
    aiHints: { range: 0.96, risk: 0.86, use: ['launch', 'punish'] },
  },
  {
    id: 'bw-anchor-crush', name: 'Anchored Crush', input: 'b+2', category: 'strike',
    stanceRequired: 'anchor',
    startup: 22, active: 4, recovery: 22,           // blk -14, hit +12
    level: 'mid', damage: 38, hitstun: 37, blockstun: 11,
    pushbackHit: 0.20, pushbackBlock: 0.32, selfPushbackBlock: 0.06,
    armor: { from: 3, to: 21, hits: 2, damageMul: 0.38 },
    hitboxes: [hb(23, 26, 'handL', 0.20, 1.34, 0.88, 1.60, 0.19, { tracking: 0.9, priority: 5 })],
    counterHit: { damageMul: 1.5, hitstunBonus: 20, forcesState: 'hitstun-stand' },
    onHit: { wallSplat: true },
    scaling: { starterValue: 0.76, cost: 0.15, minScale: 0.22 },
    tracking: 0.9, anim: 'bw.anchorcrush',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['anchor', 'armor', 'wall', 'anti-sidestep'],
    aiHints: { range: 1.07, risk: 0.78, use: ['wall', 'anti-sidestep'] },
  },

  // ===========================================================================
  // THROWS AND THROW CHAINS
  // ===========================================================================
  {
    id: 'bw-throw', name: 'Yard Toss', input: '1+3', category: 'throw',
    startup: 12, active: 2, recovery: 36,
    damage: 42, hitstun: 0, blockstun: 14,
    throwBox: { from: 13, to: 14, ox: 0.50, oy: 1.26, oz: 0, r: 0.46, catches: 'stand' },
    onHit: { knockdown: 'hard' },
    scaling: { starterValue: 0.55, cost: 0.22, minScale: 0.22 },
    meterGain: { hit: 5, block: 0, whiff: 1 },
    anim: 'bw.throw',
    sfx: { swing: 'cloth', hit: 'throw-grab', block: 'guard-light' },
    fx: { hit: 'throw-burst', trail: '' },
    tags: ['throw'],
    aiHints: { range: 0.78, risk: 0.7, use: ['mixup', 'pressure'] },
  },
  {
    id: 'bw-throw-back', name: 'Back Girder', input: 'b+1+3', category: 'throw',
    startup: 13, active: 2, recovery: 38,
    damage: 46, hitstun: 0, blockstun: 14,
    throwBox: { from: 14, to: 15, ox: 0.48, oy: 1.24, oz: 0, r: 0.46, catches: 'stand' },
    onHit: { knockdown: 'hard', wallSplat: true },
    scaling: { starterValue: 0.55, cost: 0.22, minScale: 0.22 },
    meterGain: { hit: 5, block: 0, whiff: 1 },
    anim: 'bw.throwback',
    sfx: { swing: 'cloth', hit: 'throw-grab', block: 'guard-light' },
    fx: { hit: 'throw-burst', trail: '' },
    tags: ['throw', 'wall-carry'],
    aiHints: { range: 0.76, risk: 0.75, use: ['mixup', 'wall'] },
  },
  {
    id: 'bw-holdiron', name: 'Hold Iron', input: 'qcf+1+3', category: 'command-throw',
    startup: 16, active: 2, recovery: 44,
    damage: 40, hitstun: 0, blockstun: 28,
    throwBox: { from: 17, to: 18, ox: 0.52, oy: 1.20, oz: 0, r: 0.50, catches: 'any' },
    cancelWindow: [10, 30], cancels: ['bw-hold-lift'],
    onHit: { knockdown: 'hard' },
    scaling: { starterValue: 0.55, cost: 0.24, minScale: 0.22 },
    meterGain: { hit: 6, block: 0, whiff: 2 },
    anim: 'bw.holdiron',
    sfx: { swing: 'cloth', hit: 'throw-grab', block: 'guard-light' },
    fx: { hit: 'throw-burst', trail: '' },
    tags: ['command-throw', 'chain-starter', 'anti-crouch'],
    aiHints: { range: 0.80, risk: 0.9, use: ['mixup', 'anti-crouch'] },
  },
  {
    id: 'bw-hold-lift', name: 'Hold Iron, Lift', input: '2', category: 'command-throw',
    followUpOf: 'bw-holdiron',
    startup: 4, active: 2, recovery: 34,
    damage: 26, hitstun: 0, blockstun: 10,
    throwBox: { from: 5, to: 6, ox: 0.46, oy: 1.30, oz: 0, r: 0.48, catches: 'any' },
    cancelWindow: [8, 26], cancels: ['bw-hold-slam'],
    onHit: { knockdown: 'hard' },
    scaling: { starterValue: 0.5, cost: 0.26, minScale: 0.22 },
    meterGain: { hit: 4, block: 0, whiff: 0 },
    anim: 'bw.holdlift',
    sfx: { swing: 'cloth', hit: 'throw-grab', block: 'guard-light' },
    fx: { hit: 'throw-burst', trail: '' },
    tags: ['command-throw', 'chain'],
    aiHints: { range: 0.76, risk: 0.5, use: ['pressure'] },
  },
  {
    id: 'bw-hold-slam', name: 'Hold Iron, Slam', input: '4', category: 'command-throw',
    followUpOf: 'bw-hold-lift',
    startup: 4, active: 2, recovery: 38,
    damage: 20, hitstun: 0, blockstun: 8,
    throwBox: { from: 5, to: 6, ox: 0.42, oy: 1.10, oz: 0, r: 0.48, catches: 'any' },
    onHit: { knockdown: 'hard', wallSplat: true },
    scaling: { starterValue: 0.45, cost: 0.28, minScale: 0.22 },
    meterGain: { hit: 4, block: 0, whiff: 0 },
    anim: 'bw.holdslam',
    sfx: { swing: 'cloth', hit: 'throw-slam', block: 'guard-light' },
    fx: { hit: 'ground-crack', trail: '' },
    tags: ['command-throw', 'chain-ender', 'wall'],
    aiHints: { range: 0.72, risk: 0.5, use: ['pressure', 'wall'] },
  },
  {
    id: 'bw-pitdrop', name: 'Pit Drop', input: 'qcb+1+3', category: 'command-throw',
    startup: 20, active: 2, recovery: 48,
    damage: 58, hitstun: 0, blockstun: 34,
    throwBox: { from: 21, to: 22, ox: 0.48, oy: 0.94, oz: 0, r: 0.52, catches: 'any' },
    onHit: { knockdown: 'hard' },
    scaling: { starterValue: 0.5, cost: 0.26, minScale: 0.22 },
    meterGain: { hit: 8, block: 0, whiff: 2 },
    anim: 'bw.pitdrop',
    sfx: { swing: 'cloth', hit: 'throw-slam', block: 'guard-light' },
    fx: { hit: 'ground-crack', trail: '' },
    tags: ['command-throw', 'anti-crouch', 'high-damage'],
    aiHints: { range: 0.78, risk: 0.95, use: ['mixup', 'anti-crouch'] },
  },

  // ===========================================================================
  // FINISHER — the whole Grit bar for one unblockable read.
  // ===========================================================================
  {
    id: 'bw-tectonic', name: 'Tectonic', input: '2+4', category: 'finisher',
    startup: 28, active: 5, recovery: 44,
    level: 'unblockable', damage: 112, hitstun: 44, blockstun: 0,
    pushbackHit: 0.5, pushbackBlock: 0,
    moveForward: 0.95, meterCost: 85,
    armor: { from: 1, to: 27, hits: 1, damageMul: 0.3 },
    hitboxes: [hb(29, 33, 'handR', 0.20, 1.90, 1.02, 0.30, 0.24, { tracking: 1.0, priority: 9 })],
    counterHit: { damageMul: 1.0, hitstunBonus: 0, forcesState: '' },
    onHit: { knockdown: 'hard', wallSplat: true, hitstop: 28 },
    scaling: { starterValue: 0.45, cost: 0.3, minScale: 0.22 },
    meterGain: { hit: 0, block: 0, whiff: 0 },
    tracking: 1.0, anim: 'bw.tectonic',
    sfx: { swing: 'swing-whoosh', hit: 'impact-counter', block: 'guard-heavy' },
    fx: { hit: 'finisher-shock', trail: 'arc-heavy' },
    tags: ['finisher', 'unblockable', 'meter', 'armor', 'cinematic'],
    aiHints: { range: 1.26, risk: 0.95, use: ['punish'] },
  },
];

/** Every animation identifier Bulwark's move data references. */
export const BULWARK_ANIMS = BULWARK_MOVES.map((m) => m.anim);
